import { AtomEffect, atom, useRecoilValue } from "recoil";

import { Participant, ResaleInput, RssDocument } from "@api/graphql";
import { ValidatedResaleInput } from "@components/resale_submission/csv/ParseFiles";

const RESALE_DATA_LOCAL_STORAGE_KEY = "resale_submit_data";
const RESALE_DATA_RECOIL_KEY = "multiFormDataState";
const SELECTED_RESALES = "selected_resales";
const BATCH_UPLOAD_DOCUMENT_LOCAL_STORAGE_KEY = "batch_document";
const BATCH_UPLOAD_DOCUMENT_RECOIL_KEY = "batch_document_state";
const CURATED_DOCUMENT_LOCAL_STORAGE_KEY = "curated_document";
const CURATED_DOCUMENT_RECOIL_KEY = "curated_document_state";
const SELECTED_PARTICIPANT_LOCAL_STORAGE_KEY = "selected_participant";
const SELECTED_PARTICIPANT_RECOIL_KEY = "selected_participant_state";

/**
 * Example of a dummy data atom state.
 * 1: Create a new atom with a key and a default value.
 * 2(optional): Specify any effects when needed.
 * 3: Export the atom.
 * 4: Use it in other components with useRecoilState.
 *    eg: const [dummyData, setDummyData] = useRecoilState(DummyDataAtom)
 * 5(optional): Use selector to define customized behavior for read/write state
 */
const localStorageEffect =
  <T>(key: string): AtomEffect<T> =>
  ({ setSelf, onSet }) => {
    const savedValue = localStorage.getItem(key);
    if (savedValue != null) {
      setSelf(JSON.parse(savedValue));
    }
    onSet((newValue, _, isReset) => {
      isReset ? localStorage.removeItem(key) : localStorage.setItem(key, JSON.stringify(newValue));
    });
  };

export const resaleFormData = atom<ValidatedResaleInput[]>({
  key: RESALE_DATA_RECOIL_KEY,
  default: [],
  effects: [localStorageEffect(RESALE_DATA_LOCAL_STORAGE_KEY)],
});

export const batchUploadDocument = atom<RssDocument>({
  key: BATCH_UPLOAD_DOCUMENT_RECOIL_KEY,
  default: undefined,
  effects: [localStorageEffect(BATCH_UPLOAD_DOCUMENT_LOCAL_STORAGE_KEY)],
});

export const curatedUploadDocument = atom<File>({
  key: CURATED_DOCUMENT_RECOIL_KEY,
  default: undefined,
  effects: [localStorageEffect(CURATED_DOCUMENT_LOCAL_STORAGE_KEY)],
});

export const selectedParticipant = atom<Participant>({
  key: SELECTED_PARTICIPANT_RECOIL_KEY,
  default: undefined,
  effects: [localStorageEffect(SELECTED_PARTICIPANT_LOCAL_STORAGE_KEY)],
});

export const useNextResaleIndex = (multiFormData: ResaleInput): number => {
  const useResaleFormData = useRecoilValue(resaleFormData);
  const index = useResaleFormData.findIndex(
    (resale: ResaleInput) => resale.batch_discriminator == multiFormData.batch_discriminator,
  );

  return (index >= 0 ? index : useResaleFormData.length) + 1;
};

export type ResalesType = {
  resaleId: number;
  invoice?: {
    invoice: number;
    invoice_line: number;
  };
  artistId?: number;
};

export type SelectedResalesType = {
  [key: string]: ResalesType[];
};

export const selectedResales = atom<SelectedResalesType>({
  key: SELECTED_RESALES,
  default: {},
});
